/**
 * Shared helpers for handling image drops/pastes into the terminal.
 *
 * Three input cases all funnel into the same output: the image is saved
 * as a project attachment via backend.files.saveAttachment(), then the
 * resulting on-disk path is returned for the caller to insert into the PTY.
 *
 *   1. File object from drag-drop or clipboard items (we have raw bytes)
 *   2. Path string from drag-drop text/plain (we read the file via backend)
 *
 * Falls through to null if the input isn't an image — caller should then
 * insert the original text path as before.
 */
import type { IBackend } from '@/services/backend/IBackend';
import { useProjectStore } from '@/stores/projectStore';

/** Resolve a project's database ID from its filesystem path (case-insensitive). */
export function projectIdFromPath(projectPath: string): string | null {
  const normalized = projectPath.replace(/\\/g, '/').toLowerCase();
  const projects = useProjectStore.getState().projects;
  const match = projects.find(
    (p) => p.root_path.replace(/\\/g, '/').toLowerCase() === normalized,
  );
  return match?.id ?? null;
}

const IMAGE_EXTENSIONS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'ico',
]);

const MAX_PASTE_BYTES = 10 * 1024 * 1024; // 10 MB — matches saveAttachment limit

export function isImagePath(path: string): boolean {
  const ext = path.split('.').pop()?.toLowerCase();
  return ext ? IMAGE_EXTENSIONS.has(ext) : false;
}

export function isImageFile(file: File | { type?: string; name?: string }): boolean {
  if (file.type && file.type.startsWith('image/')) return true;
  if (file.name) return isImagePath(file.name);
  return false;
}

/** Convert a Uint8Array to base64 (chunked to avoid stack overflow). */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Save a File/Blob as a project attachment and return the saved on-disk
 * path. Returns null on failure or oversized input.
 */
export async function saveImageAsAttachment(
  file: File,
  projectId: string,
  backend: IBackend,
): Promise<string | null> {
  if (file.size > MAX_PASTE_BYTES) return null;

  const buffer = await file.arrayBuffer();
  const base64 = bytesToBase64(new Uint8Array(buffer));
  const fileName = file.name || `pasted-${Date.now()}.png`;

  return await backend.files.saveAttachment(projectId, fileName, base64);
}

/**
 * Given a path string pointing to an image on disk, read the file via the
 * backend and re-save it as a project attachment. Returns the new attachment
 * path, or null if the path isn't an image / can't be read.
 *
 * This is used when the user drags an image from the in-app Files panel
 * (which only provides text/plain with the path) and expects the agent to
 * actually receive the image content rather than a string path.
 */
export async function importImagePathAsAttachment(
  path: string,
  projectId: string,
  backend: IBackend,
): Promise<string | null> {
  if (!isImagePath(path)) return null;

  try {
    const base64 = await backend.files.readFileBase64(projectId, path);
    if (!base64) return null;
    const fileName = path.split(/[\\/]/).pop() || `imported-${Date.now()}.png`;
    return await backend.files.saveAttachment(projectId, fileName, base64);
  } catch {
    return null;
  }
}

/**
 * Quote a path for shell insertion if it contains spaces.
 */
export function quoteIfSpaced(path: string): string {
  return path.includes(' ') ? `"${path}"` : path;
}
